import { useToast } from '@ais-ui/ui-core';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import {
  getEmptySessions,
  getEmptyWebsockets,
  initializeWebsockets,
  Session,
  Sessions,
  User,
  ValidatedSessions,
  WebSockets,
  createClientWithCredentials,
  createSession,
  retireSessions,
} from './createSession';
import {
  clearCredentials,
  hasStoredCredentials,
  getSavedCredentials,
  saveCredentials,
  describeStoredSession,
  storedSessionTelemetry,
  type StoredSessionInfo,
} from './tokenStorage';
import { useQueryClient } from '@tanstack/react-query';
import { useProfile } from '@ais-ui/profile';
import { msalInstance } from './config/authConfig';
import { useReconnect } from './useReconnect';
import { AuthenticationResult } from '@azure/msal-browser/src/response/AuthenticationResult';
import { useBasePath } from '@ais-ui/custom-hooks';
import { categorizeLogoutReason, SessionType } from './sessionErrorUtils';
import { primeMapView } from './primeMapView';

export interface UserCredentials {
  username: string;
  password: string;
}

type AuthMethod = 'credentials' | 'azure';

export interface AuthRoutePaths {
  mapPath: string;
  mfaBeforeLoginPath: string;
  changePasswordPath: string;
}

/**
 * Extra context carried by a forced logout so Sentry telemetry can show not
 * just *that* a user was logged out, but *why*: which underlying websocket
 * session triggered it (for reconnect-driven logouts), and how long the
 * grace period ran before giving up. Purely additive — omit for a plain
 * `logout(reason)` call.
 */
export interface LogoutContext {
  sessionType?: SessionType;
  gracePeriodMs?: number;
  /** What the last `checkSessionValid` said before the grace period ended. */
  sessionValidity?: LogoutSessionValidity;
}

/**
 * Outcome of the `checkSessionValid` RPC that preceded a grace-period logout.
 * `rejected` means the backend refused the session (`false` or a session
 * signal in `error`); `timed_out`/`error` are inconclusive checks that never
 * got a definite answer; `in_progress` means a check was still awaiting its
 * answer when the grace period ended; `not_checked` means the transport never came back
 * far enough to ask (e.g. socket not open).
 */
export interface LogoutSessionValidity {
  outcome: 'rejected' | 'timed_out' | 'error' | 'in_progress' | 'not_checked';
  /** Error message from the check, when it failed rather than answered. */
  error?: string;
  /** How many checks ran during the grace period. */
  checks: number;
}

/**
 * Snapshot of the browser/transport state at the instant of logout, captured
 * so a forced logout in Sentry carries enough context to tell *what kind* of
 * failure it was without guessing. The load-bearing discriminator is
 * `olSocketReadyState`, read live at logout time: an `open` socket with a
 * rejected session points at a server-side session loss (e.g. the olServer
 * instance that owned the sessionId was rolled/drained — the client
 * reconnected but the new instance returned `SessionNotFound`), whereas
 * `none`/`closed`/`connecting` points at a transport that never came back.
 * Combined with `wasOnline` (was the client's own network up?), this lets a
 * mass logout be characterised after the fact. `olServerUrl` is the
 * *client-side* WebSocket endpoint only — behind a load balancer it is the
 * same shared host for every olServer pod, so it identifies the environment,
 * NOT which backend instance served the session. All fields optional and
 * purely additive — omit for a plain `logout(reason)` call.
 */
export interface LogoutEnvironment {
  /** `WebSocket.readyState` of the olSession socket at logout time. */
  olSocketReadyState?: number;
  /** Client-side olServer WebSocket endpoint, e.g. `wss://<host>/ws/olserver`.
   * This is the shared load-balancer host (built from the browser hostname in
   * `initializeOlWebSocket`), NOT a per-pod/instance identifier — every pod
   * reports the same value, so it can't be used to correlate to a specific
   * olServer instance. */
  olServerUrl?: string;
  /** `navigator.onLine` — whether the client's own network was up. */
  wasOnline?: boolean;
  /** `document.visibilityState` — a backgrounded tab throttles reconnect
   * timers, which can itself cause a grace-period give-up. */
  documentVisibility?: string;
  /** `navigator.connection.effectiveType` when available (Chromium only). */
  connectionType?: string;
}

interface AuthProviderContextState {
  credentials?: UserCredentials;
  /** The signed-in user's username, reliably set on both fresh login and a
   * restored session (see `useProfile`/`restoreUserSession`) — unlike
   * `credentials`, which is only populated on localhost. Prefer this over
   * `credentials?.username` for anything that needs to identify the current
   * user in production, e.g. session-validity checks. */
  username: string;
  /** The single canonical "is this user logged in" signal — derived from
   * `username`, which `login`/`restoreUserSession` set and `logout` clears.
   * Use this for any auth gate (e.g. protected-route redirects).
   *
   * Deliberately NOT the same thing as `sessions.olSession` being truthy:
   * that reflects only whether the live olserver socket happens to be
   * connected right now, which `useGracefulLogout` intentionally lets go
   * falsy for up to a grace period while it reconnects, without the user
   * being logged out. Gating a route/component on `sessions.olSession`
   * instead of `isAuthenticated` races that grace period and force-logs-out
   * users it was meant to protect (see CLASSIC-10283). */
  isAuthenticated: boolean;
  sessions: Sessions;
  authMethod?: AuthMethod;
  login: (sessions: ValidatedSessions, user: User) => void;
  loginWithAzure: () => Promise<AuthenticationResult | undefined>;
  logout: (reason?: string, context?: LogoutContext) => void;
  reconnect: (server: string) => void;
  storeCredentials: (credentials: UserCredentials) => void;
  twoFactorEnabled: boolean;
  setTwoFactorEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  initiateWebsockets: () => Promise<WebSockets>;
  setSessions: React.Dispatch<React.SetStateAction<Sessions>>;
  attemptAutoLogin: () => Promise<boolean>;
  isInitializing: boolean;
  routePaths: AuthRoutePaths;
}

const AuthContext = createContext<AuthProviderContextState | undefined>(
  undefined,
);

export interface AuthProviderProps {
  children: ReactNode;
  onUserLogout?: () => void;
  initialCredentials?: UserCredentials;
  routePaths: AuthRoutePaths;
  /** Injected from websockets lib to avoid circular dependency */
  checkSessionValid?: (
    session: Session,
    sessionUuid: string,
    userName: string,
  ) => Promise<boolean>;
  /** Injected from websockets lib to avoid circular dependency */
  initializeMapView?: (session: Session) => Promise<void>;
}

export const AuthProvider = ({
  children,
  initialCredentials,
  routePaths,
  checkSessionValid,
  initializeMapView,
}: AuthProviderProps) => {
  const { hostname } = useBasePath();
  const [credentials, setCredentials] = useState<UserCredentials | undefined>(
    initialCredentials,
  );
  const [authMethod, setAuthMethod] = useState<AuthMethod>('credentials');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    if (!msalInstance) {
      return;
    }
    msalInstance.initialize().catch((error) => {
      console.error('Failed to initialize MSAL:', error);
      Sentry.captureException(error);
    });
  }, []);

  const { email, username, setEmail, setUsername, setGroups, setUserId } =
    useProfile();
  // Derived, not stored — see `AuthProviderContextState.isAuthenticated` for
  // why this must stay independent of `sessions.olSession`.
  const isAuthenticated = Boolean(username);
  const [webSockets, setWebSockets] =
    useState<WebSockets>(getEmptyWebsockets());
  const [sessions, setSessions] = useState<Sessions>(getEmptySessions());
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const storeCredentials = useCallback(
    (credentials: UserCredentials) => {
      setCredentials(credentials);
    },
    [setCredentials],
  );

  const logout = useCallback(
    (reason = '', context?: LogoutContext) => {
      // Every logout — manual or forced — is reported to Sentry so we can
      // measure how often it happens and who's affected. Captured before any
      // state below is cleared, so the event still carries the user/session
      // it happened to.
      //
      // Snapshot the transport/browser state *now* (before `setSessions` wipes
      // it) so a forced logout carries enough context to tell an olserver
      // instance-drain (socket still open, session rejected server-side) apart
      // from a client that simply went offline. See `LogoutEnvironment`.
      const olSocket = sessions?.olSession?.webSocket;
      const connection = (
        navigator as Navigator & { connection?: { effectiveType?: string } }
      ).connection;
      const environment: LogoutEnvironment = {
        olSocketReadyState: olSocket?.readyState,
        olServerUrl: olSocket?.url,
        wasOnline: navigator.onLine,
        documentVisibility:
          typeof document === 'undefined'
            ? undefined
            : document.visibilityState,
        connectionType: connection?.effectiveType,
      };

      const { message, options } = buildLogoutSentryEvent(
        reason,
        context,
        {
          username,
          email,
          sessionId: sessions?.sessionId,
        },
        environment,
        describeStoredSession(
          getStoredCredentialsForSession(sessions?.sessionId),
        ),
      );
      Sentry.captureMessage(message, options);

      // After the snapshot above has read the socket state. Nothing else
      // closes these: left open they stay GC roots re-dispatching server
      // pushes, and a late close would be read as the next login's session
      // dropping.
      retireSessions(sessions, 'user logged out');

      if (authMethod === 'azure' && msalInstance) {
        msalInstance.logoutPopup().catch(console.error);
        msalInstance.clearCache();
      }
      setCredentials(undefined);
      setSessions(getEmptySessions());
      setGroups([]);
      // Clear the identity fields `isAuthenticated` derives from, so it
      // correctly flips back to false after logout instead of leaving a
      // stale username behind.
      setUsername('');
      setEmail('');
      setUserId('');
      queryClient.clear();
      Sentry.setUser(null);

      // Clear saved credentials and sessionId
      clearCredentials();

      // Store logout reason for login form to display
      if (typeof reason === 'string' && reason) {
        sessionStorage.setItem('logoutReason', reason);
      }

      navigate('/'); // Navigate to login page after logout

      if (typeof reason === 'string' && reason) {
        toast({
          className: 'logout-success-toast',
          title: 'Logged out',
          description: reason,
        });
      }
    },
    [
      authMethod,
      email,
      navigate,
      queryClient,
      sessions,
      setEmail,
      setGroups,
      setUserId,
      setUsername,
      toast,
      username,
    ],
  );

  const reconnect = useReconnect(sessions, setSessions, initializeMapView);

  const login = useCallback(
    (sessions: ValidatedSessions, user: User) => {
      setSessions(sessions);

      setGroups(user.groups);
      setEmail(user.email);
      setUsername(user.username);
      setUserId(user.userId);

      // Store username and sessionId
      // On localhost, also preserve password for easier re-authentication
      if (sessions?.sessionId) {
        const isLocalhost = window.location.hostname.includes('localhost');
        const existingCredentials = getSavedCredentials();

        // On localhost, use password from credentials state (set by storeCredentials before login)
        // or fall back to existing localStorage password
        const passwordToSave = isLocalhost
          ? credentials?.password || existingCredentials?.password
          : undefined;

        saveCredentials({
          username: user.username,
          sessionId: sessions.sessionId,
          password: passwordToSave,
        });

        // Also update credentials state with password on localhost
        if (isLocalhost && passwordToSave) {
          setCredentials({
            username: user.username,
            password: passwordToSave,
          });
        }
      }

      Sentry.setUser({
        id: user.username,
        username: user.username,
        email: user.email,
        groups: user.groups,
        session_id: sessions?.sessionId,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      });

      // Guarantee at least one 100%-sampled transaction with user context
      Sentry.startSpan(
        { name: 'user.authenticated', op: 'auth', forceTransaction: true },
        () => {},
      );

      navigate(routePaths.mapPath);
    },
    [credentials, navigate, setEmail, setGroups, setUserId, setUsername],
  );

  const loginWithAzure = useCallback(async () => {
    try {
      if (!msalInstance) {
        throw new Error('MSAL not initialized');
      }

      const response = await msalInstance.loginPopup({
        scopes: ['https://graph.microsoft.com/.default'],
      });
      setAuthMethod('azure');
      // Clear any stored credentials from previous credential-based logins
      clearCredentials();

      // Note: sessionId will be stored when login() is called after Azure authentication completes

      return response;
    } catch (error) {
      console.error('Azure login failed:', error);
      toast({
        title: 'Login Failed',
        description: 'Failed to login with Azure',
        variant: 'destructive',
      });
    }
  }, [toast]);

  const initiateWebsockets = useCallback(async (): Promise<WebSockets> => {
    if (webSockets) {
      const webSocketProperties = [
        'olWebsocket',
        'dbWebsocket',
        'replayWebsocket',
      ] as const;

      for (const prop of webSocketProperties) {
        const socket = webSockets[prop];
        if (socket && socket.readyState !== WebSocket.CLOSED) {
          socket.close(1000, 'Reinitializing connections');
        }
      }
    }

    const initiatedWebsockets = await initializeWebsockets();

    setWebSockets(initiatedWebsockets);

    return initiatedWebsockets;
  }, [webSockets]);

  const tryRestoreSessionById = useCallback(
    async (
      savedCredentials: NonNullable<ReturnType<typeof getSavedCredentials>>,
    ): Promise<boolean> => {
      if (!savedCredentials.sessionId) {
        reportRestoreFailure('no_session_id', savedCredentials);
        return false;
      }

      try {
        const initiatedWebsockets = await initializeWebsockets();
        setWebSockets(initiatedWebsockets);

        const { olSession, dbSession, replaySession } = createSessionsWithId(
          initiatedWebsockets,
          savedCredentials.sessionId,
        );

        if (!olSession) {
          reportRestoreFailure('no_ol_session', savedCredentials);
          return false;
        }

        if (checkSessionValid) {
          const validity = await checkSessionValidWithRetries(
            checkSessionValid,
            olSession,
            savedCredentials.sessionId,
            savedCredentials.username,
          );

          if (validity !== 'valid') {
            reportRestoreFailure(
              validity === 'invalid' ? 'session_invalid' : 'check_failed',
              savedCredentials,
            );
            return false;
          }
        }

        // Prime the backend mapview (best-effort - see primeMapView.ts).
        // Deliberately not wrapped in this function's own try/catch: a
        // priming failure must not be treated the same as an invalid
        // session (that used to bounce a perfectly good restored session
        // to the login screen - CLASSIC-10280).
        await primeMapView(initializeMapView, olSession, 'restore');

        const restoredSessions: ValidatedSessions = {
          olSession,
          dbSession,
          replaySession,
          sessionId: savedCredentials.sessionId,
        };
        setSessions(restoredSessions);

        // On localhost, restore credentials state with password for API auth headers
        const isLocalhost = window.location.hostname.includes('localhost');
        if (isLocalhost && savedCredentials.password) {
          setCredentials({
            username: savedCredentials.username,
            password: savedCredentials.password,
          });
        }

        restoreUserSession(
          savedCredentials.username,
          savedCredentials.sessionId,
          setUsername,
          navigate,
          routePaths.mapPath,
        );

        return true;
      } catch (error) {
        console.error('Error checking session validity:', error);
        reportRestoreFailure('error', savedCredentials, error);
        return false;
      }
    },
    [navigate, setUsername, checkSessionValid, initializeMapView],
  );

  const tryLoginWithPassword = useCallback(
    async (
      savedCredentials: NonNullable<ReturnType<typeof getSavedCredentials>>,
    ): Promise<boolean> => {
      if (!savedCredentials.password) {
        return false;
      }

      const isLocalhost = window.location.hostname.includes('localhost');
      if (!isLocalhost) {
        console.log('Password-based login is only available on localhost');
        clearCredentials();
        return false;
      }

      try {
        const initiatedWebsockets = await initializeWebsockets();
        setWebSockets(initiatedWebsockets);

        const result = await createClientWithCredentials(
          {
            username: savedCredentials.username,
            password: savedCredentials.password,
          },
          initiatedWebsockets,
        );

        if (result.sessions.sessionId && result.user.userId) {
          setCredentials({
            username: savedCredentials.username,
            password: savedCredentials.password,
          });

          login(result.sessions as ValidatedSessions, result.user);
          return true;
        } else {
          reportRestoreFailure('password_login_failed', savedCredentials);
          clearCredentials();
          return false;
        }
      } catch (error) {
        console.error('Password-based auto-login failed:', error);
        reportRestoreFailure('password_login_failed', savedCredentials, error);
        clearCredentials();
        return false;
      }
    },
    [login],
  );

  const attemptAutoLogin = useCallback(async (): Promise<boolean> => {
    if (!hasStoredCredentials()) {
      return false;
    }

    const savedCredentials = getSavedCredentials();
    if (!savedCredentials) {
      return false;
    }

    const sessionRestored = await tryRestoreSessionById(savedCredentials);
    if (sessionRestored) {
      return true;
    }

    const passwordLoginSuccessful =
      await tryLoginWithPassword(savedCredentials);
    if (passwordLoginSuccessful) {
      return true;
    }

    return false;
  }, [tryRestoreSessionById, tryLoginWithPassword]);

  // Attempt automatic login on mount if there are saved credentials
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      // If we already have sessions, no need to auto-login
      if (sessions.olSession) {
        setIsInitializing(false);
        return;
      }

      // `isInitializing` is what holds the AuthenticatedRoute guard on its
      // loading branch instead of redirecting to login, so this must clear
      // even if attemptAutoLogin ever threw instead of resolving to false.
      try {
        await attemptAutoLogin();
      } finally {
        if (mounted) {
          setIsInitializing(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
    };
  }, []); // Only run on mount

  return (
    <AuthContext.Provider
      value={{
        credentials,
        username,
        isAuthenticated,
        sessions,
        reconnect,
        login,
        loginWithAzure,
        logout,
        authMethod,
        storeCredentials,
        twoFactorEnabled,
        setTwoFactorEnabled,
        initiateWebsockets,
        setSessions,
        attemptAutoLogin,
        isInitializing,
        routePaths,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return authContext;
};

interface LogoutTelemetryUser {
  username?: string;
  email?: string;
  sessionId?: string;
}

/**
 * `WebSocket.readyState` → low-cardinality label for the `logout_socket_state`
 * Sentry tag. Numeric literals (not `WebSocket.OPEN` etc.) so this module has
 * no load-time dependency on the `WebSocket` global: 0 CONNECTING, 1 OPEN,
 * 2 CLOSING, 3 CLOSED (WHATWG spec).
 */
const SOCKET_STATE_LABELS: Record<number, string> = {
  0: 'connecting',
  1: 'open',
  2: 'closing',
  3: 'closed',
};

function socketStateLabel(readyState: number | undefined): string {
  if (readyState === undefined) {
    return 'none';
  }
  return SOCKET_STATE_LABELS[readyState] ?? 'unknown';
}

/**
 * Build the `Sentry.captureMessage` arguments for a `logout()` call.
 * A pure function (no Sentry/React dependency of its own) so the exact
 * shape of the telemetry — message, tags, extra — can be unit tested
 * without rendering `AuthProvider`.
 */
export function buildLogoutSentryEvent(
  reason: string,
  context: LogoutContext | undefined,
  { username, email, sessionId }: LogoutTelemetryUser,
  environment?: LogoutEnvironment,
  storedSession?: StoredSessionInfo,
): { message: string; options: Parameters<typeof Sentry.captureMessage>[1] } {
  const stored = storedSessionTelemetry(storedSession);
  return {
    message: `User logged out: ${reason || 'manual'}`,
    options: {
      level: 'warning',
      tags: {
        logout_trigger: reason ? 'forced' : 'manual',
        logout_reason_category: categorizeLogoutReason(reason),
        ...(context?.sessionType && {
          logout_session_type: context.sessionType,
        }),
        ...(context?.sessionValidity && {
          logout_validity_check: context.sessionValidity.outcome,
        }),
        // Low-cardinality, queryable diagnostics (kept as tags so they can be
        // filtered/aggregated in Sentry search, unlike `extra`). Gate on the
        // *environment* being present, not on `olSocketReadyState` — when a
        // snapshot was taken but the olSession is already gone (the dominant
        // `reconnect_giveup` case: transport dropped and never came back),
        // `socketStateLabel(undefined)` is `none`, and that "no transport at
        // logout" fact is exactly the signal we want to keep, not drop.
        ...(environment && {
          logout_socket_state: socketStateLabel(environment.olSocketReadyState),
        }),
        ...(environment?.wasOnline !== undefined && {
          logout_was_online: String(environment.wasOnline),
        }),
        ...(environment?.documentVisibility !== undefined && {
          logout_document_visibility: environment.documentVisibility,
        }),
        ...stored.tags,
      },
      extra: {
        ...stored.extra,
        username,
        email,
        sessionId,
        ...(context?.gracePeriodMs !== undefined && {
          gracePeriodMs: context.gracePeriodMs,
        }),
        ...(context?.sessionValidity && {
          validityChecks: context.sessionValidity.checks,
          ...(context.sessionValidity.error !== undefined && {
            validityError: context.sessionValidity.error,
          }),
        }),
        // Higher-cardinality context (instance URL, raw readyState, network
        // type) — kept in `extra` rather than tags.
        ...(environment?.olServerUrl !== undefined && {
          olServerUrl: environment.olServerUrl,
        }),
        ...(environment?.olSocketReadyState !== undefined && {
          olSocketReadyState: environment.olSocketReadyState,
        }),
        ...(environment?.connectionType !== undefined && {
          connectionType: environment.connectionType,
        }),
      },
    },
  };
}

/**
 * The credentials key is shared across tabs, so another tab may have replaced
 * the entry with a different session; describing that one would attach its age
 * and release to this session's event. With no session ID there is nothing to
 * match, and password-only credentials (no session ID either) must not.
 */
export function getStoredCredentialsForSession(sessionId: string | undefined) {
  if (!sessionId) return undefined;
  const stored = getSavedCredentials();
  return stored?.sessionId === sessionId ? stored : undefined;
}

export type RestoreFailureReason =
  | 'no_session_id'
  | 'no_ol_session'
  | 'session_invalid'
  | 'check_failed'
  | 'password_login_failed'
  | 'error';

interface RestoreEnvironment {
  wasOnline?: boolean;
  documentVisibility?: DocumentVisibilityState;
}

/**
 * Build the `Sentry.captureMessage` arguments for a failed session restore.
 *
 * A failed restore on page load drops the user on the login screen without
 * going through `logout()`, so it never shows up as a `User logged out`
 * event even though, to the user, it looks exactly like being logged out.
 * The Sentry user is attached explicitly because it isn't on the scope yet
 * this early in page load.
 */
export function buildRestoreFailureSentryEvent(
  reason: RestoreFailureReason,
  { username, sessionId }: { username: string; sessionId?: string },
  environment?: RestoreEnvironment,
  error?: unknown,
  storedSession?: StoredSessionInfo,
): { message: string; options: Parameters<typeof Sentry.captureMessage>[1] } {
  const stored = storedSessionTelemetry(storedSession);
  return {
    message: `Session restore failed: ${reason}`,
    options: {
      // Credentials without a sessionId are an ordinary state (e.g. mid MFA
      // or password-change flow), not a failure worth alerting on.
      level: reason === 'no_session_id' ? 'info' : 'warning',
      user: { id: username, username },
      tags: {
        restore_failure_reason: reason,
        ...(environment?.wasOnline !== undefined && {
          restore_was_online: String(environment.wasOnline),
        }),
        ...(environment?.documentVisibility !== undefined && {
          restore_document_visibility: environment.documentVisibility,
        }),
        ...stored.tags,
      },
      extra: {
        ...stored.extra,
        username,
        sessionId,
        ...(error !== undefined && {
          errorMessage: error instanceof Error ? error.message : String(error),
        }),
      },
    },
  };
}

function reportRestoreFailure(
  reason: RestoreFailureReason,
  savedCredentials: NonNullable<ReturnType<typeof getSavedCredentials>>,
  error?: unknown,
): void {
  const { username, sessionId } = savedCredentials;
  const environment: RestoreEnvironment = {
    wasOnline: navigator.onLine,
    documentVisibility:
      typeof document === 'undefined' ? undefined : document.visibilityState,
  };
  const { message, options } = buildRestoreFailureSentryEvent(
    reason,
    { username, sessionId },
    environment,
    error,
    describeStoredSession(savedCredentials),
  );
  Sentry.captureMessage(message, options);
}

export type SessionValidity = 'valid' | 'invalid' | 'check_failed';

const CHECK_SESSION_VALID_RETRY_ATTEMPTS = 3;
const CHECK_SESSION_VALID_RETRY_DELAY_MS = 1500;

/**
 * Retry a `checkSessionValid` RPC before concluding a session is invalid.
 *
 * `checkSessionValid` can throw for reasons unrelated to the session actually
 * being invalid (e.g. a transient backend error). Without retries, a single
 * blip on page refresh silently fails `tryRestoreSessionById` and bounces a
 * perfectly good session to the login screen. An explicit `false` result is
 * `invalid`; a thrown error is retried a few times first and, if every attempt
 * throws, reported as `check_failed` so the two can be told apart.
 */
export async function checkSessionValidWithRetries(
  checkSessionValid: NonNullable<AuthProviderProps['checkSessionValid']>,
  session: Session,
  sessionUuid: string,
  userName: string,
): Promise<SessionValidity> {
  for (
    let attempt = 1;
    attempt <= CHECK_SESSION_VALID_RETRY_ATTEMPTS;
    attempt++
  ) {
    try {
      const isValid = await checkSessionValid(session, sessionUuid, userName);
      return isValid ? 'valid' : 'invalid';
    } catch (error) {
      const isLastAttempt = attempt === CHECK_SESSION_VALID_RETRY_ATTEMPTS;
      console.warn(
        `checkSessionValid attempt ${attempt}/${CHECK_SESSION_VALID_RETRY_ATTEMPTS} failed${
          isLastAttempt ? ', giving up' : ', retrying'
        }:`,
        error,
      );
      if (isLastAttempt) {
        return 'check_failed';
      }
      await new Promise((resolve) =>
        setTimeout(resolve, CHECK_SESSION_VALID_RETRY_DELAY_MS),
      );
    }
  }
  return 'check_failed';
}

const createSessionsWithId = (
  initiatedWebsockets: WebSockets,
  sessionId: string,
) => {
  const olSession = createSession(
    initiatedWebsockets.olServerAndClient,
    initiatedWebsockets.olWebsocket,
    sessionId,
    'olSession',
  );
  const dbSession = createSession(
    initiatedWebsockets.dbServerAndClient,
    initiatedWebsockets.dbWebsocket,
    sessionId,
    'dbSession',
  );
  const replaySession = createSession(
    initiatedWebsockets.replayServerAndClient,
    initiatedWebsockets.replayWebsocket,
    sessionId,
    'replaySession',
  );

  return { olSession, dbSession, replaySession };
};

const restoreUserSession = (
  username: string,
  sessionId: string,
  setUsername: (username: string) => void,
  navigate: (path: string) => void,
  mapPath: string,
) => {
  setUsername(username);

  Sentry.setUser({
    id: username,
    username: username,
    session_id: sessionId,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
  });

  // Guarantee at least one 100%-sampled transaction with user context
  Sentry.startSpan(
    { name: 'user.authenticated', op: 'auth', forceTransaction: true },
    () => {},
  );

  navigate(mapPath);
};

export const useCredentials = () => {
  const authContext = useAuth();

  const { credentials } = authContext;

  // if (credentials == null) {
  //   throw new Error('useCredentials must be used after user is logged in');
  // }

  return credentials;
};
